import { useCallback, useMemo } from "react";
import sortBy from "lodash.sortby";

import { useSlackSenderApi } from "services/api/SlackSenderApi";
import { EmailAddressMultiSelect } from "modules/workflows/components/EmailAddressMultiSelect";
import { useEmployeeFields } from "../../../../../../../../../employees/hooks/useEmployeeFields";

type SlackUsersSelectV2Props = {
  senderId?: string;
  initialSelected: string[];
  error?: boolean;
  onSelect: (newValue: string[]) => unknown;
  readOnly?: boolean;
  journeyAttributes?: { [key: string]: string };
  addButtonText?: string;
  isJourneyEditor?: boolean;
};

export const SlackUserSelectV2 = ({
  senderId,
  initialSelected,
  error,
  onSelect,
  readOnly,
  isJourneyEditor,
  journeyAttributes,
  addButtonText
}: SlackUsersSelectV2Props) => {
  const slackSenderApi = useSlackSenderApi();
  const { data: slackUsers, isLoading: isUsersLoading } = slackSenderApi.listUsers(
    { senderId: senderId || "" },
    { enabled: !!senderId }
  ).query;

  const { fields } = useEmployeeFields();

  const getEmailPropertyOptions = useCallback(() => {
    if (!fields) return [];
    const options = fields
      .filter(prop => prop.type === "email")
      .map(prop => ({
        key: prop.key,
        title: prop.title,
        description: journeyAttributes ? journeyAttributes[prop.key] : undefined
      }));
    return sortBy(options, "title");
  }, [journeyAttributes, fields]);

  const options = useMemo(() => {
    if (isUsersLoading) return [];
    const propertyOptions = getEmailPropertyOptions();
    const userOptions =
      slackUsers?.map(user => ({ key: user.id, title: user.realName, description: "" })) || [];

    return [...propertyOptions, ...userOptions];
  }, [isUsersLoading, slackUsers, getEmailPropertyOptions]);

  const selectedOptions = useMemo(() => {
    return options.filter(option => initialSelected.find(id => option.key === id));
  }, [options, initialSelected]);

  return (
    <EmailAddressMultiSelect
      isJourneyEditor={isJourneyEditor}
      readOnly={readOnly}
      initialOptions={options}
      onChange={nextSelected => onSelect(nextSelected)}
      addButtonText={addButtonText ?? "Add receiver"}
      initialSelectedOptions={selectedOptions}
      error={error ? "Required field" : undefined}
      addButtonReadOnlyTooltip="Start by selecting a sender"
    />
  );
};
